import { Typography } from "@mui/material";
import { ISODateString } from "@smartrr/shared/entities/ISODateString";
import { IOrganization } from "@smartrr/shared/entities/Organization";
import { IPurchaseState, PurchaseStateAndDelivery } from "@smartrr/shared/entities/PurchaseState";
import { SellingPlanConfigs } from "@smartrr/shared/entities/SellingPlanGroup";
import { viewShopifyId } from "@smartrr/shared/utils/ensureShopifyGid";
import { toDays } from "@smartrr/shared/utils/renderViewDate";
import { localize, typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import React from "react";

import { IModalOptions } from "@customer-app/state/actionCreators/modal";

import { IModal, ModalComponent } from "..";
import { getFrequencyString } from "../../AquariusInner/components/Subscription/utils";

export function getMainTitle(
  localActiveComponent: keyof IModal,
  localActiveComponentProps: IModal[ModalComponent],
  delivery: PurchaseStateAndDelivery | null
) {
  switch (localActiveComponent) {
    case "MANAGE_SUBSCRIPTIONS": {
      const purchaseState = delivery?.purchaseState;
      return `#${viewShopifyId(purchaseState?.shopifyId)}`;
    }
    case "ORDER_HISTORY": {
      return `${typedLocalize("orderHistory.order")} ${
        (localActiveComponentProps as IModal["ORDER_HISTORY"]).order.shopifyOrderLabel
      }`;
    }
    case "ORDER_OPTIONS": {
      return localize(
        (localActiveComponentProps as IModal["ORDER_OPTIONS"]).showUpcomingOrderButton
          ? "subscriptions.yourSubscriptions.upcomingOrdersModal.managingOrder"
          : "subscriptions.yourSubscriptions.orderOptionsModal.modalTitle"
      );
    }
    case "UPCOMING_ORDERS": {
      return typedLocalize("subscriptions.yourSubscriptions.upcomingOrdersModal.modalTitle");
    }
    case "CHANGE_EMAIL": {
      return typedLocalize("settings.personalInformation.changeEmail");
    }
    case "CHANGE_BIRTHDAY": {
      return typedLocalize("settings.personalInformation.birthdayModalTitle");
    }
    case "REDEEM_PRODUCT":
    case "REDEEM_INCENTIVE": {
      return typedLocalize("loyalty.redeem");
    }
    case "HOW_TO_EARN_POINTS": {
      return (localActiveComponentProps as IModal["HOW_TO_EARN_POINTS"]).title;
    }
    case "EDIT_BUNDLE": {
      return typedLocalize("editBundle.modalTitle");
    }
    case "SELECT_MODAL": {
      return typedLocalize("selectModal.modalTitle");
    }
    case "PRODUCT_DESCRIPTION": {
      const { configType } = localActiveComponentProps as IModal["PRODUCT_DESCRIPTION"];
      if (configType === SellingPlanConfigs.SUBSCRIPTION) {
        return `#${viewShopifyId(delivery?.purchaseState.shopifyId)}`;
      }
      return typedLocalize("subscriptions.addToYourNextOrder.title");
    }
    case "ADDON_LIST": {
      const inManageSubscriptionView = (localActiveComponentProps as IModal["ADDON_LIST"])!.addItemAsSubscription;

      if (inManageSubscriptionView) {
        const purchaseState = delivery?.purchaseState;
        return `#${viewShopifyId(purchaseState?.shopifyId)}`;
      }
      return localize(
        (localActiveComponentProps as IModal["ORDER_OPTIONS"]).showUpcomingOrderButton
          ? "subscriptions.yourSubscriptions.upcomingOrdersModal.managingOrder"
          : "subscriptions.yourSubscriptions.orderOptionsModal.modalTitle"
      );
    }

    default: {
      return "";
    }
  }
}

export function getSubtitle(
  localActiveComponent: keyof IModal,
  localActiveComponentProps: IModal[ModalComponent],
  delivery: PurchaseStateAndDelivery | null,
  organization: IOrganization
) {
  switch (localActiveComponent) {
    case "PRODUCT_DESCRIPTION": {
      const { configType } = localActiveComponentProps as IModal["PRODUCT_DESCRIPTION"];
      if (configType === SellingPlanConfigs.SUBSCRIPTION) {
        return typedLocalize("subscriptions.yourSubscriptions.manageSubscriptionModal.shipsEvery", {
          deliveryFrequency: delivery ? toDays(getFreq(delivery.purchaseState, organization)) : "",
        });
      }
      return "";
    }
    case "MANAGE_SUBSCRIPTIONS": {
      return typedLocalize("subscriptions.yourSubscriptions.manageSubscriptionModal.shipsEvery", {
        deliveryFrequency: delivery ? toDays(getFreq(delivery.purchaseState, organization)) : "",
      });
    }
    case "ORDER_HISTORY": {
      return typedLocalize("utils.dates.toViewDateLong", {
        date: ISODateString.fromString(
          (localActiveComponentProps as IModal["ORDER_HISTORY"])?.order.orderProcessedDate
        ).toJSDate(),
      });
    }
    case "ORDER_OPTIONS": {
      return localize(
        (localActiveComponentProps as IModal["ORDER_OPTIONS"]).showUpcomingOrderButton
          ? typedLocalize("utils.dates.toViewDateLong", {
              date: delivery?.date!,
            })
          : ""
      );
    }
    case "ADDON_LIST": {
      const { addItemAsSubscription: inManageSubscriptionView } =
        (localActiveComponentProps as IModal["ADDON_LIST"])!;

      if (inManageSubscriptionView) {
        return typedLocalize("subscriptions.yourSubscriptions.manageSubscriptionModal.shipsEvery", {
          deliveryFrequency: delivery ? toDays(getFreq(delivery.purchaseState, organization)) : "",
        });
      }
      return localize(
        (localActiveComponentProps as IModal["ORDER_OPTIONS"]).showUpcomingOrderButton
          ? typedLocalize("utils.dates.toViewDateLong", {
              date: delivery?.date!,
            })
          : ""
      );
    }
    default: {
      return "";
    }
  }
}

export function getCaption(
  localActiveComponent: keyof IModal,
  localActiveComponentProps: IModal[ModalComponent],
  delivery: PurchaseStateAndDelivery | null,
  openModal: (component: ModalComponent, props?: IModal[ModalComponent], options?: IModalOptions) => void
) {
  switch (localActiveComponent) {
    case "MANAGE_SUBSCRIPTIONS": {
      const unpauseDate = delivery?.purchaseState.unpauseDate;
      if (unpauseDate) {
        return (
          <Typography variant="caption1">
            {typedLocalize("subscriptions.yourSubscriptions.manageSubscriptionModal.pauseView.resumeDateCaption")}{" "}
            {typedLocalize("utils.dates.toViewDateLong", {
              date: ISODateString.fromString(unpauseDate).toJSDate(),
            })}
            {"."}
            <Typography
              variant="caption1"
              component="p"
              sx={{
                textDecoration: "underline",
                "&:hover": {
                  cursor: "pointer",
                },
              }}
              onClick={() =>
                openModal("MANAGE_SUBSCRIPTIONS", {
                  ...(localActiveComponentProps as IModal["MANAGE_SUBSCRIPTIONS"]),
                  initialView: "Pause",
                })
              }
            >
              {typedLocalize(
                "subscriptions.yourSubscriptions.manageSubscriptionModal.pauseView.resumeDateCaptionButton"
              )}
            </Typography>
          </Typography>
        );
      }

      return <Typography variant="caption1"></Typography>;
    }

    default: {
      return <Typography variant="caption1"></Typography>;
    }
  }
}

const getFreq = (purchaseState: IPurchaseState, organization: IOrganization) => {
  return getFrequencyString(purchaseState.schedule, organization!);
};
